// Story 3.5: Transparent calculation breakdown display
// Shows the full pipeline: route → distance → deduction → claimable → rate → amount
import { MapPin } from 'lucide-react';
import type { MileageBreakdown as Breakdown } from '@rhpc/shared';

interface MileageBreakdownProps {
  origin: string;
  destination: string;
  vehicleType?: string | null;
  breakdown: Breakdown;
  hasOverride?: boolean;
  overrideReason?: string | null;
}

function km(val: number): string {
  return `${val.toFixed(2)} km`;
}

function formatAmount(val: number): string {
  return new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(val);
}

export function MileageBreakdown({
  origin,
  destination,
  vehicleType,
  breakdown,
  hasOverride,
  overrideReason,
}: MileageBreakdownProps) {
  return (
    <div className="rounded-xl bg-surface-2/30 border border-border p-4 text-sm space-y-2">
      {/* Route */}
      <div className="flex items-start gap-2 font-medium text-text mb-3">
        <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <span className="flex-1">
          {origin}
          <span className="mx-1 text-text-2">→</span>
          {destination}
        </span>
        {hasOverride && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-medium shrink-0">
            Manual Override
          </span>
        )}
      </div>

      {/* Step-by-step grid */}
      <div className="space-y-1.5">
        {/* Total distance */}
        <div className="flex items-center justify-between">
          <span className="text-text-2">Total distance</span>
          <span className="font-mono font-medium">{km(breakdown.totalDistanceKm)}</span>
        </div>

        {/* Deduction (conditionally shown) */}
        {breakdown.deductionApplied && (
          <>
            <div className="flex items-center justify-between text-danger">
              <span className="flex items-center gap-1">
                <span>−</span>
                <span>Home-office deduction</span>
              </span>
              <span className="font-mono font-medium">− {km(breakdown.homeOfficeDeductionKm)}</span>
            </div>
            <p className="text-xs text-text-2 italic pl-4">{breakdown.deductionExplanation}</p>
            <div className="border-t border-border/60 my-1" />
          </>
        )}

        {/* Claimable distance */}
        <div className="flex items-center justify-between font-medium text-ok ">
          <span className="flex items-center gap-1">
            <span>=</span>
            <span>Claimable distance</span>
          </span>
          <span className="font-mono">{km(breakdown.claimableDistanceKm)}</span>
        </div>

        {/* Rate */}
        <div className="flex items-center justify-between text-text-2">
          <span className="flex items-center gap-1">
            <span>×</span>
            <span>Rate ({vehicleType ?? 'Car'})</span>
          </span>
          <span className="font-mono">RM {breakdown.ratePerKm.toFixed(2)}/km</span>
        </div>

        <div className="border-t border-border my-1.5" />

        {/* Final amount */}
        <div className="flex items-center justify-between">
          <span className="font-semibold text-text text-base">=&nbsp;Reimbursement</span>
          <span className="font-bold text-primary text-lg font-mono">{formatAmount(breakdown.amountMyr)}</span>
        </div>
      </div>

      {/* Override reason */}
      {hasOverride && overrideReason && (
        <p className="mt-2 pt-2 border-t border-border text-xs text-text-2">
          <span className="font-medium">Override reason:</span> {overrideReason}
        </p>
      )}
    </div>
  );
}

// Compact loading skeleton version
export function MileageBreakdownSkeleton() {
  return (
    <div className="rounded-xl bg-surface-2/30 border border-border p-4 space-y-3 animate-pulse">
      <div className="h-4 bg-surface-2 rounded w-3/4" />
      <div className="space-y-2">
        <div className="h-3 bg-surface-2 rounded w-full" />
        <div className="h-3 bg-surface-2 rounded w-4/5" />
        <div className="h-3 bg-surface-2 rounded w-full" />
        <div className="h-4 bg-surface-2 rounded w-1/2 mt-2" />
      </div>
    </div>
  );
}
